import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Box, Tooltip } from '@mui/material';
import { useState } from 'react';
import { theme } from '@/theme';
import SideNavIcon from './SideNavIcon';
import SideNavWrappedLabel from './SideNavWrappedLabel';

interface Props {
    name: string;
    logoSrc?: string;
    isExpanded: boolean;
    isInteractive: boolean;
    /** Shows a lock: the firm's client data needs the firm's permission */
    isPrivate?: boolean;
    onClick?: () => void;
}

export default function SideNavLawFirm({
    name,
    logoSrc,
    isExpanded,
    isInteractive,
    isPrivate = false,
    onClick,
}: Props) {
    const [isLabelTruncated, setIsLabelTruncated] = useState(false);

    const handleClick = () => {
        if (!isInteractive) return;
        onClick?.();
    };

    const containerStyles = {
        position: 'relative',
        padding: '0 16px',
        borderLeft: '2px solid transparent',
        cursor: isInteractive ? 'pointer' : 'default',
        color: theme.palette.sidenav.contrastText,
        width: isExpanded ? '200px' : '48px',
        boxSizing: 'border-box',
        userSelect: 'none',
        transition: 'width 0.2s ease-in',
        ...(isInteractive && {
            '&:hover': {
                color: theme.palette.sidenav.contrastTextActive,
            },
        }),
    };

    const rowStyles = {
        display: 'grid',
        gridTemplateColumns: isExpanded ? (isPrivate ? '24px 1fr 16px' : '24px 1fr') : '24px',
        alignItems: 'center',
        gap: '4px',
        minHeight: '40px',
        padding: '4px 0',
        boxSizing: 'border-box',
        justifyContent: isExpanded ? 'initial' : 'center',
    };

    const renderIcon = () => {
        if (logoSrc) {
            return (
                <img
                    alt=""
                    src={logoSrc}
                    style={{
                        width: '24px',
                        height: '24px',
                        objectFit: 'contain',
                        borderRadius: '2px',
                    }}
                />
            );
        }

        return <SideNavIcon icon="ph:building-apartment" />;
    };

    // Collapsed: always show name (label is hidden). Expanded: only if truncated.
    const fullName = isPrivate ? `${name} (private)` : name;
    const tooltipTitle = !isExpanded || isLabelTruncated ? fullName : null;

    return (
        <Tooltip placement="right" title={tooltipTitle}>
            <Box onClick={handleClick} sx={containerStyles}>
                <Box sx={rowStyles}>
                    {renderIcon()}
                    {isExpanded && (
                        <SideNavWrappedLabel text={name} onTruncatedChange={setIsLabelTruncated} />
                    )}
                    {isExpanded && isPrivate && (
                        <LockOutlinedIcon titleAccess="Private firm" sx={{ fontSize: 16 }} />
                    )}
                </Box>
            </Box>
        </Tooltip>
    );
}
